import crypto from "../include/crypto";
import { ListenerInfo, ServerResponse, UploadFileMeta } from "./types";
import SecurequServerBase from "./Base";
import fileScaner from "../include/file-scaner";
import { HTTPMethods } from "../client/types";
import { chunkFile, getFileId, totalChunks } from "../include/file";
export * from './types'

class SecurequServer extends SecurequServerBase {

   async handshake(body: any, clientInfo: any) {
      const data: any = await crypto.decryptBuffer(body, clientInfo.secret)
      const serverSecret = await this.getSecret()
      if (!data) throw new Error("Invalid handshake data");
      if (data.hash !== clientInfo.hash) {
         throw new Error("Invalid handshake");
      } else if (data.secret !== clientInfo.secret) {
         throw new Error("Invalid handshake secret");
      } else if (Date.now() - data.clientTime > 30000) { // 30 seconds
         throw new Error("Handshake expired");
      }

      const serverTime = new Date();
      const clientDate = new Date(data.clientTime);
      const offsetMs = clientDate.getTime() - serverTime.getTime();

      let info = {
         dev: this.config.mode === 'development',
         timeDiffarenc: offsetMs,
         maxFileSize: this.config.file?.maxFilesize || null,
         checkFileType: this.config.file?.checkFileType || false,
         signeture: await crypto.encrypt({
            expire: Date.now() + 30000, // 30 seconds
            hash: clientInfo.hash
         }, serverSecret)
      }
      throw info
   }

   async uploadFile(file: File, onProgress?: (p: number) => void): Promise<UploadFileMeta> {
      if (!this.config.file?.upload) throw new Response("Upload not supported", { status: 403 });

      const fileId = await getFileId(file);
      const chunkSize = this.config.file.chunkSize
      const filemeta: UploadFileMeta = {
         fileId: fileId,
         name: file.name,
         size: file.size,
         type: file.type,
         totalChunks: totalChunks(file, chunkSize),
         chunkIndex: 0,
         isFinish: false
      }

      for await (let { chunk, chunkIndex } of chunkFile(file, chunkSize)) {
         if (this.config.file.checkFileType && chunkIndex === 0 && !fileScaner(chunk).valid) {
            throw new Error("Unknown or unsupported file type")
         }
         filemeta.chunkIndex = chunkIndex;
         filemeta.isFinish = chunkIndex + 1 === filemeta.totalChunks;
         await this.config.file.upload(chunk, filemeta);
         if (onProgress) {
            const progress = Math.floor(((chunkIndex + 1) / filemeta.totalChunks) * 100)
            onProgress(progress);
         }
      }
      return filemeta
   }

   async deleteFile(fileId: string): Promise<void> {
      if (!this.config.file?.delete) throw new Response("Delete not supported", { status: 403 })
      await this.config.file.delete(fileId);
   }

   async listen(url: string, { body, headers, metadata }: ListenerInfo): Promise<ServerResponse> {
      const signeture = headers['x-signeture'] || '';
      const origin = headers['x-origin'] || '';
      const method: HTTPMethods = headers['x-method'] || 'POST' as any
      let clientInfo = await this.clientInfo(url, origin)


      if (!clientInfo) {
         return {
            status: 403,
            value: "Client not allowed",
            contentType: 'text/plain'
         }
      }
      let path = clientInfo.path
      const isDev = path !== '/' && this.config.mode === 'development'
      try {
         if (path === '/' && method === "POST") {
            await this.handshake(body, clientInfo)
         }

         if (path === '/' && method === 'GET') throw new Response("Invalid method", { status: 405 })

         if (!signeture) {
            throw new Response("Signeture is required", { status: 403 });
         }
         const decryptSigneture = await crypto.decrypt(signeture, clientInfo.secret) as any
         if (!decryptSigneture) throw new Response("Invalid Signeture", { status: 403 });
         if (decryptSigneture.expire < Date.now()) throw new Response("Request expired", { status: 403 });

         await this.isValidSigneture(decryptSigneture.signeture, clientInfo.hash)

         let data: any = null
         if (body) {
            if (isDev) {
               const decoder = new TextDecoder();
               const text = decoder.decode(body).trim();
               if (text) {
                  try {
                     data = JSON.parse(text)
                  } catch (error) {

                  }
               }
            } else {
               data = await crypto.decryptBuffer(body, clientInfo.secret)
            }
         }

         if (path === '/') {

            const fileConfig = this.config.file

            // handle file upload
            if (method === "PUT") {
               if (!fileConfig?.upload) throw new Response("Upload not supported", { status: 403 })

               const filemeta: UploadFileMeta = data.filemeta
               const chunk: Uint8Array = data.chunk

               if (filemeta.chunkIndex === 0 && fileConfig.checkFileType && !fileScaner(chunk)) {
                  throw new Error("Unknown or unsupported file type");
               }

               await fileConfig.upload(chunk, filemeta)
               throw { success: true }
            } else if (method === "DELETE") {
               await this.deleteFile(data.fileId)
               throw { success: true }
            } else {
               throw new Response("Invalid method", { status: 405 });
            }
         }

         await this.handleRequest({
            method,
            path,
            body: data,
            searchParams: clientInfo.searchParams,
            params: {}
         }, metadata);

      } catch (info: any) {
         if (this.config.debug) {
            let data: any = null
            if (body) {
               if (isDev) {
                  const decoder = new TextDecoder();
                  const text = decoder.decode(body).trim();
                  if (text) {
                     try {
                        data = JSON.parse(text)
                     } catch (error) {

                     }
                  }
               } else {
                  data = await crypto.decryptBuffer(body, clientInfo.secret)
               }
            }
            const colors = {
               reset: "\x1b[0m",
               cyan: "\x1b[36m",
               yellow: "\x1b[33m",
               green: "\x1b[32m",
               magenta: "\x1b[35m",
               blue: "\x1b[34m",
               bold: "\x1b[1m"
            };

            console.log(`${colors.blue}======================${colors.reset}`);
            console.log(`${colors.bold}${colors.cyan}URL:${colors.reset} ${url}`);
            console.log(`${colors.bold}${colors.yellow}METHOD:${colors.reset} ${method}`);
            console.log(`${colors.bold}${colors.magenta}SIGNATURE:${colors.reset} ${signeture}`);

            console.log(`${colors.bold}${colors.green}REQUEST BODY:${colors.reset}`);
            console.log(data);

            console.log(`${colors.bold}${colors.blue}RESPONSE:${colors.reset}`);
            console.log(info);

            console.log(`${colors.blue}======================${colors.reset}`);
         }
         const isError = info instanceof Error
         const isResponse = info instanceof Response
         let value = isError ? info.message : (isResponse ? await info.text() : info);
         if (isDev) {
            return {
               status: isError ? 404 : (info?.status || 200),
               value: JSON.stringify(value),
               contentType: 'application/json'
            };
         }
         return {
            status: isError ? 404 : (info?.status || 200),
            value: await crypto.encryptBuffer(value, clientInfo.secret),
            contentType: 'application/octet-stream'
         };
      }

      if (isDev) {
         return {
            status: 404,
            value: "404 page not found",
            contentType: 'text/plain'
         };
      }

      return {
         status: 404,
         value: await crypto.encryptBuffer("404 page not found", clientInfo.secret),
         contentType: 'application/octet-stream'
      };
   }

}

export default SecurequServer;